/**
 * De WebSocket met het Durable Object van één spel, als externe store voor React
 * (`useSyncExternalStore`). Los van React, zodat hij zonder browser te testen is.
 *
 * Het verloop: open de socket, stuur `hallo`, ontvang `welkom` en daarna `staat` (of
 * `onbekendSpel`). Na elke wijziging door wie dan ook komt een nieuwe `staat`. Valt de
 * verbinding weg, dan verbindt hij opnieuw met een oplopende wachttijd; de laatste staat
 * blijft zolang zichtbaar.
 *
 * Niet in het origineel: een hartslag. Een verbinding die half dood is (wifi weg, laptop
 * dicht) merkt de browser soms minutenlang niet; het scherm bleef dan "verbonden" met
 * stilstaande cijfers. Na een stille periode stuurt de verbinding daarom een `PING`. Blijft
 * het antwoord uit, dan geldt de verbinding als dood en komt er een nieuwe.
 */
import { PING, PONG, type ClientBericht, type ServerBericht, type SpelStaat } from '@ame/shared';

/**
 * - `verbinden`: nog geen antwoord van de server;
 * - `verbonden`: de staat is binnen en de verbinding staat open;
 * - `onbekend`: er is geen spel met dit id (of het is verwijderd);
 * - `verbroken`: de verbinding is weg en wordt opnieuw gelegd. `staat` is dan de laatst
 *   bekende, of `null` als er nooit een binnenkwam.
 */
export type SpelStatus = 'verbinden' | 'verbonden' | 'onbekend' | 'verbroken';

/** Wat `useSpel()` teruggeeft. Onveranderlijk: bij elke wijziging komt er een nieuw object. */
export type SpelMomentopname = {
  readonly staat: SpelStaat | null;
  readonly status: SpelStatus;
  /** De `versie` van de laatste staat; loopt bij elke wijziging op. 0 zolang er geen is. */
  readonly versie: number;
};

/**
 * Een mislukte actie. `message` is bij `soort: 'spel'` letterlijk de fouttekst van de
 * server (zoals "Deze naam bestaat al!"), te vergelijken met een `Meteor.Error`.
 */
export class ActieFout extends Error {
  override name = 'ActieFout';
  constructor(
    message: string,
    /** `spel`: de server weigerde; `tijd`: geen antwoord binnen de tijd; `verbinding`: weg. */
    readonly soort: 'spel' | 'tijd' | 'verbinding',
  ) {
    super(message);
  }
}

/**
 * Spel-id's zijn zes tekens a-z0-9, zoals het origineel ze maakte (`Random.id(6)` in
 * kleine letters). De Worker weigert al het andere met een 400; daar hoeft geen socket
 * voor open.
 */
export function isGeldigSpelId(spelId: string): boolean {
  return /^[a-z0-9]{6}$/.test(spelId);
}

/** De URL van de WebSocket, op dezelfde host als de pagina (in dev via de Vite-proxy). */
export function spelSocketUrl(spelId: string, locatie: Location = window.location): string {
  const protocol = locatie.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${locatie.host}/api/games/${encodeURIComponent(spelId)}/ws`;
}

/**
 * Niet in het origineel: voor de schermen van de spelleider. Zegt de server dat deze
 * verbinding geen rechten (meer) heeft, dan vraagt de verbinding `isIngelogd`. Is de sessie
 * weg, dan `opVerlopen` (het inlogscherm). Geldt hij nog, bijvoorbeeld na opnieuw inloggen in
 * een ander tabblad, dan verbindt hij opnieuw: de nieuwe verbinding krijgt de sessie van nu.
 */
export type SpelleiderSessie = {
  isIngelogd: () => Promise<boolean>;
  opVerlopen: () => void;
};

export type VerbindingOpties = {
  /** Te vervangen in tests. Standaard `new WebSocket(url)`. */
  maakSocket?: (url: string) => WebSocket;
  /** Te vervangen in tests. Standaard `spelSocketUrl(spelId)`. */
  url?: string;
  /** Hoe lang een actie op antwoord wacht, in ms. */
  actieTijdslimiet?: number;
  /** Alleen voor de schermen van de spelleider; zie `SpelleiderSessie`. */
  spelleider?: SpelleiderSessie;
};

/** Opnieuw verbinden na 0,5 s, 1 s, 2 s, ... tot hoogstens 15 s, met wat spreiding. */
const EERSTE_WACHTTIJD = 500;
const LANGSTE_WACHTTIJD = 15_000;
const STANDAARD_TIJDSLIMIET = 10_000;

/**
 * De hartslag: elke 5 s kijken. Na 15 s zonder bericht een `PING`. Een ping of een nieuwe
 * verbinding die na 10 s nog geen antwoord heeft, geldt als dood.
 */
const HARTSLAG = 5_000;
const PING_NA = 15_000;
const ANTWOORD_BINNEN = 10_000;

/** Zo lang mag het laatste bericht oud zijn als het tabblad terugkomt. Ouder: opnieuw verbinden. */
const OUD = PING_NA + ANTWOORD_BINNEN;

/** Hoogstens zo vaak opnieuw verbinden omdat de rechten van de spelleider ontbraken. */
const RECHTEN_OPNIEUW_NA = 30_000;

// `WebSocket.CONNECTING` en `.OPEN` als getal, zodat een nagemaakte socket in tests volstaat.
const CONNECTING = 0;
const OPEN = 1;

/** De server sluit zo als het spel is verwijderd; dan heeft opnieuw verbinden geen zin. */
const VERWIJDERD = { code: 1000, reden: 'Spel verwijderd' };

type OpenActie = {
  bericht: Extract<ClientBericht, { type: 'actie' }>;
  /** De socket waarover de actie ging, of `null` zolang hij nog wacht. */
  verstuurdOp: WebSocket | null;
  resolve: (waarde: unknown) => void;
  reject: (fout: ActieFout) => void;
  timer: ReturnType<typeof setTimeout>;
};

let volgnummer = 0;

export class SpelVerbinding {
  readonly spelId: string;
  private readonly opties: Required<Omit<VerbindingOpties, 'url' | 'spelleider'>> &
    Pick<VerbindingOpties, 'url' | 'spelleider'>;
  private momentopname: SpelMomentopname;
  private readonly luisteraars = new Set<() => void>();
  private socket: WebSocket | null = null;
  private actief = false;
  private poging = 0;
  private herstelTimer: ReturnType<typeof setTimeout> | undefined;
  private hartslagTimer: ReturnType<typeof setInterval> | undefined;
  /** Wanneer (ms) de huidige socket voor het laatst iets zei, of geopend werd. */
  private laatstGehoord = 0;
  /** Wanneer (ms) de ping ging die nog op antwoord wacht, of `null`. */
  private pingOp: number | null = null;
  private rechtenBezig = false;
  private rechtenHerverbonden = -Infinity;
  private readonly openActies = new Map<string, OpenActie>();

  constructor(spelId: string, opties: VerbindingOpties = {}) {
    this.spelId = spelId;
    this.opties = {
      maakSocket: opties.maakSocket ?? ((url) => new WebSocket(url)),
      url: opties.url,
      actieTijdslimiet: opties.actieTijdslimiet ?? STANDAARD_TIJDSLIMIET,
      spelleider: opties.spelleider,
    };
    this.momentopname = {
      staat: null,
      status: isGeldigSpelId(spelId) ? 'verbinden' : 'onbekend',
      versie: 0,
    };
  }

  // Pijlfuncties, zodat React ze los kan doorgeven zonder `bind`.
  subscribe = (luisteraar: () => void): (() => void) => {
    this.luisteraars.add(luisteraar);
    return () => this.luisteraars.delete(luisteraar);
  };

  getSnapshot = (): SpelMomentopname => this.momentopname;

  /** Begin met verbinden. Mag vaker; React's StrictMode roept start-stop-start aan. */
  start(): void {
    if (this.actief) return;
    this.actief = true;
    if (!isGeldigSpelId(this.spelId)) return;
    window.addEventListener('online', this.verbindNu);
    document.addEventListener('visibilitychange', this.verbindNu);
    this.hartslagTimer = setInterval(this.hartslag, HARTSLAG);
    this.open();
  }

  /** Sluit de verbinding en laat openstaande acties mislukken. */
  stop(): void {
    if (!this.actief) return;
    this.actief = false;
    window.removeEventListener('online', this.verbindNu);
    document.removeEventListener('visibilitychange', this.verbindNu);
    clearTimeout(this.herstelTimer);
    clearInterval(this.hartslagTimer);
    this.sluitSocket();
    for (const [id, actie] of this.openActies) {
      this.rondAf(id, actie, () =>
        actie.reject(new ActieFout('De verbinding is gesloten.', 'verbinding')),
      );
    }
  }

  /**
   * Voer een actie uit op de server, zoals `Meteor.call(naam, ...args)` zonder spel-id.
   * Zonder open verbinding wacht de actie tot die er weer is (binnen de tijdslimiet).
   */
  actie(naam: string, args: unknown[]): Promise<unknown> {
    if (!isGeldigSpelId(this.spelId)) {
      return Promise.reject(new ActieFout('Dit spel bestaat niet.', 'spel'));
    }
    const id = `${Date.now().toString(36)}-${(volgnummer += 1)}`;
    return new Promise((resolve, reject) => {
      const actie: OpenActie = {
        bericht: { type: 'actie', id, naam, args },
        verstuurdOp: null,
        resolve,
        reject,
        timer: setTimeout(() => {
          this.rondAf(id, actie, () =>
            reject(new ActieFout('De server reageert niet. Probeer het opnieuw.', 'tijd')),
          );
          // Niet in het origineel: geen antwoord betekent meestal een dode verbinding. Was er
          // nog geen verbinding, dan meteen opnieuw proberen in plaats van de wachttijd uit te
          // zitten.
          if (actie.verstuurdOp === this.socket) this.herverbind();
        }, this.opties.actieTijdslimiet),
      };
      this.openActies.set(id, actie);
      this.verstuurWachtende();
    });
  }

  /**
   * Gooit de huidige verbinding weg en legt een nieuwe, met de melding in beeld zolang dat
   * duurt. Een actie die al weg was, mislukt (zie `verbindingWeg`). Zonder socket: meteen
   * opnieuw proberen in plaats van de wachttijd uit te zitten.
   */
  private herverbind(): void {
    if (!this.actief) return;
    if (this.socket) {
      this.sluitSocket();
      this.verbindingWeg(false);
    } else if (this.momentopname.status !== 'onbekend') {
      this.poging = 0;
      this.open();
    }
  }

  private open(): void {
    clearTimeout(this.herstelTimer);
    const socket = this.opties.maakSocket(this.opties.url ?? spelSocketUrl(this.spelId));
    this.socket = socket;
    this.laatstGehoord = Date.now();
    this.pingOp = null;

    socket.addEventListener('open', () => {
      if (this.socket !== socket) return;
      this.stuur({ type: 'hallo' });
      this.verstuurWachtende();
    });
    socket.addEventListener('message', (gebeurtenis) => {
      if (this.socket !== socket || typeof gebeurtenis.data !== 'string') return;
      // Elk bericht telt als teken van leven, ook een `PONG`.
      this.laatstGehoord = Date.now();
      this.pingOp = null;
      if (gebeurtenis.data === PONG) return;
      const bericht = leesBericht(gebeurtenis.data);
      if (bericht) this.verwerk(bericht);
    });
    socket.addEventListener('close', (gebeurtenis) => {
      if (this.socket !== socket) return;
      this.socket = null;
      this.verbindingWeg(
        gebeurtenis.code === VERWIJDERD.code && gebeurtenis.reason === VERWIJDERD.reden,
      );
    });
    // Na een `error` volgt altijd een `close`; daar wordt het afgehandeld.
  }

  private verwerk(bericht: ServerBericht): void {
    switch (bericht.type) {
      case 'welkom':
        this.poging = 0;
        // Niet in het origineel: een scherm van de spelleider zonder rechten.
        if (bericht.spelleider === false) this.rechtenKwijt();
        return;
      case 'staat':
        // Over één socket komen berichten op volgorde binnen, dus de laatste is de nieuwste.
        this.zet({ staat: bericht.staat, status: 'verbonden', versie: bericht.versie });
        return;
      case 'onbekendSpel':
        // De socket blijft open: wordt het spel alsnog gemaakt, dan komt de staat vanzelf.
        this.zet({ staat: null, status: 'onbekend', versie: 0 });
        return;
      case 'resultaat': {
        const actie = this.openActies.get(bericht.id);
        if (!actie) return;
        this.rondAf(bericht.id, actie, () =>
          bericht.ok
            ? actie.resolve(bericht.waarde)
            : actie.reject(new ActieFout(bericht.fout, 'spel')),
        );
        if (!bericht.ok && bericht.inloggen) this.rechtenKwijt();
        return;
      }
    }
  }

  private verbindingWeg(verwijderd: boolean): void {
    // Een actie die al weg was kan wel of niet zijn uitgevoerd; niet blind opnieuw sturen.
    for (const [id, actie] of this.openActies) {
      if (!actie.verstuurdOp) continue;
      this.rondAf(id, actie, () =>
        actie.reject(new ActieFout('De verbinding met het spel viel weg.', 'verbinding')),
      );
    }
    if (!this.actief) return;

    if (verwijderd) {
      this.zet({ staat: null, status: 'onbekend', versie: 0 });
      return;
    }

    this.zet({ ...this.momentopname, status: 'verbroken' });
    const wachttijd = Math.min(LANGSTE_WACHTTIJD, EERSTE_WACHTTIJD * 2 ** this.poging);
    this.poging += 1;
    // Spreiding, zodat niet alle schermen tegelijk terugkomen na een storing.
    this.herstelTimer = setTimeout(() => this.open(), wachttijd * (0.8 + Math.random() * 0.4));
  }

  /**
   * Bij `online` of als het tabblad weer zichtbaar wordt meteen opnieuw proberen, in plaats
   * van de oplopende wachttijd uit te zitten. Tablets zetten een tabblad op de achtergrond
   * vaak stil. Staat er nog een socket, dan kan die intussen dood zijn zonder dat de browser
   * het weet: is het laatste bericht oud, dan meteen een nieuwe, anders eerst een ping.
   */
  private verbindNu = (): void => {
    if (!this.actief || document.visibilityState === 'hidden') return;
    if (this.socket) {
      if (Date.now() - this.laatstGehoord >= OUD) this.herverbind();
      else this.ping();
      return;
    }
    if (this.momentopname.status === 'onbekend') return;
    this.poging = 0;
    this.open();
  };

  /** Niet in het origineel: zie de uitleg bovenaan en `HARTSLAG`. */
  private hartslag = (): void => {
    const socket = this.socket;
    if (!socket) return;
    const nu = Date.now();
    if (socket.readyState !== OPEN) {
      // Een verbinding die maar niet opengaat, is net zo dood.
      if (nu - this.laatstGehoord >= ANTWOORD_BINNEN) this.herverbind();
    } else if (this.pingOp !== null) {
      if (nu - this.pingOp >= ANTWOORD_BINNEN) this.herverbind();
    } else if (nu - this.laatstGehoord >= PING_NA) {
      this.ping();
    }
  };

  private ping(): void {
    if (this.socket?.readyState !== OPEN || this.pingOp !== null) return;
    this.pingOp = Date.now();
    this.socket.send(PING);
  }

  /**
   * Niet in het origineel: de server zegt dat deze verbinding de acties van de spelleider
   * niet (meer) mag doen. Alleen op de schermen van de spelleider; zie `SpelleiderSessie`.
   * Hoogstens eens per `RECHTEN_OPNIEUW_NA` opnieuw verbinden, zodat een verbinding die om
   * een andere reden geen rechten krijgt niet blijft rondgaan.
   */
  private rechtenKwijt(): void {
    const sessie = this.opties.spelleider;
    if (!sessie || this.rechtenBezig) return;
    this.rechtenBezig = true;
    sessie
      .isIngelogd()
      .then((ingelogd) => {
        if (!this.actief) return;
        if (!ingelogd) {
          sessie.opVerlopen();
        } else if (Date.now() - this.rechtenHerverbonden >= RECHTEN_OPNIEUW_NA) {
          this.rechtenHerverbonden = Date.now();
          this.herverbind();
        }
      })
      .catch(() => {
        // De server is niet te bereiken; dat laat de verbinding zelf al zien.
      })
      .finally(() => {
        this.rechtenBezig = false;
      });
  }

  private verstuurWachtende(): void {
    const socket = this.socket;
    if (socket?.readyState !== OPEN) return;
    for (const actie of this.openActies.values()) {
      if (actie.verstuurdOp) continue;
      actie.verstuurdOp = socket;
      this.stuur(actie.bericht);
    }
  }

  private stuur(bericht: ClientBericht): void {
    this.socket?.send(JSON.stringify(bericht));
  }

  private sluitSocket(): void {
    const socket = this.socket;
    this.socket = null;
    if (!socket) return;
    if (socket.readyState === CONNECTING) {
      // Sluiten tijdens het verbinden geeft een waarschuwing in de console; wacht op `open`.
      socket.addEventListener('open', () => socket.close(1000));
    } else if (socket.readyState === OPEN) {
      socket.close(1000);
    }
  }

  private rondAf(id: string, actie: OpenActie, afronding: () => void): void {
    clearTimeout(actie.timer);
    this.openActies.delete(id);
    afronding();
  }

  private zet(momentopname: SpelMomentopname): void {
    this.momentopname = momentopname;
    for (const luisteraar of this.luisteraars) luisteraar();
  }
}

/** Een binnenkomend bericht, of null als het geen JSON-object met een `type` is. */
function leesBericht(tekst: string): ServerBericht | null {
  try {
    const data: unknown = JSON.parse(tekst);
    return typeof data === 'object' && data !== null && 'type' in data
      ? (data as ServerBericht)
      : null;
  } catch {
    return null;
  }
}
